import { apiClient } from "@nabble/sdk";
import { useState } from "react";
import type { StepResponse } from "../../../auth/session";
import { createPasskey } from "../../../auth/webauthn";
import type { TotpEnrollment } from "../../../hooks/useTotpSecret";
import { errorMessage } from "../../../errors";

export type EnrollmentMethod = "totp" | "passkey";

export type EnrollmentResponse = StepResponse & { recoveryCodes: string[] };

export interface MfaEnrollment {
  /** `null` while the person is still choosing. */
  choice: EnrollmentMethod | null;
  choose: (method: EnrollmentMethod) => void;
  /** Back to the list of methods, forgetting the last error. */
  back: () => void;
  loading: boolean;
  error: string | null;
  /** Set once a factor is enrolled: the recovery codes to save and the step that follows. */
  enrolled: EnrollmentResponse | null;
  startTotp: () => Promise<TotpEnrollment>;
  onTotpStartError: (err: unknown) => void;
  confirmTotp: (code: string) => Promise<void>;
  enrollPasskey: () => Promise<void>;
}

/** Enrolling the first second factor: the method chosen, its ceremony, and the recovery codes it yields. */
export function useMfaEnrollment(): MfaEnrollment {
  const [choice, setChoice] = useState<EnrollmentMethod | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState<EnrollmentResponse | null>(null);

  function back(): void {
    setChoice(null);
    setError(null);
  }

  async function confirmTotp(code: string): Promise<void> {
    setError(null);
    try {
      setEnrolled(await apiClient<EnrollmentResponse>("/api/auth/mfa/enroll/totp/confirm", { method: "POST", body: { code } }));
    } catch (err) {
      setError(errorMessage(err, "Wrong code. Check your device's clock and try again."));
    }
  }

  async function enrollPasskey(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const options = await apiClient("/api/auth/mfa/enroll/passkey", { method: "POST" });
      const credential = await createPasskey(options as Record<string, unknown>);
      setEnrolled(await apiClient<EnrollmentResponse>("/api/auth/mfa/enroll/passkey/confirm", { method: "POST", body: { credential } }));
    } catch (err) {
      setError(errorMessage(err, "We couldn't register the passkey. If you canceled, try again."));
    } finally {
      setLoading(false);
    }
  }

  return {
    choice,
    choose: setChoice,
    back,
    loading,
    error,
    enrolled,
    startTotp: () => apiClient<TotpEnrollment>("/api/auth/mfa/enroll/totp", { method: "POST" }),
    onTotpStartError: (err) => setError(errorMessage(err, "We couldn't start the setup. Reload the page.")),
    confirmTotp,
    enrollPasskey,
  };
}
